import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ArrowRightLeft, Archive, Download, FileSpreadsheet, Plus, Printer, Tag, TicketX } from 'lucide-react'
import { Alerta, Boton, Busqueda, Campo, Cargando, Paginacion, Seleccion } from '../../components/kit'
import { BarraSeleccion } from '../../components/inventario/BarraSeleccion'
import { DialogoAgregarBienes } from '../../components/inventario/DialogoAgregarBienes'
import { DialogoBaja } from '../../components/inventario/DialogoBaja'
import { DialogoSolicitarTraslado } from '../../components/inventario/DialogoSolicitarTraslado'
import { DialogoTraslado } from '../../components/inventario/DialogoTraslado'
import { DialogoEtiquetas } from '../../components/inventario/Etiquetas'
import { TablaBienes } from '../../components/inventario/TablaBienes'
import { useAuth } from '../../contexts/AuthProvider'
import { useBienes, useDescargarPlanilla, useMarcarEtiquetas, useParaEtiquetas } from '../../queries/useInventario'
import { Bien, EstadoBien, EstadoFisico, Revisar, Sector, TipoBien } from '../../types/inventario'
import { mensajeDeError } from '../../utils/errores'
import { ESTADO, ESTADO_FISICO, REVISAR, SECTOR, TIPO } from '../../utils/inventario'

const FILTROS = ['q', 'tipo', 'estado', 'estado_fisico', 'sector', 'revisar', 'recinto', 'teletrabajo'] as const
type Filtro = (typeof FILTROS)[number]

/**
 * Todos los bienes, con búsqueda y filtros. Los filtros viven en la URL: desde Inicio se
 * llega con «?revisar=repetidos», y el enlace se puede compartir o volver atrás. Quien no
 * administra bienes solo consulta y, marcando varios, pide su traslado.
 */
export default function Bienes() {
  const admin = useAuth().puede('administra_bienes')
  const [params, setParams] = useSearchParams()
  const filtros = Object.fromEntries(FILTROS.map((f) => [f, params.get(f) ?? ''])) as Record<Filtro, string>
  const pagina = Number(params.get('pagina')) || 1
  const bienes = useBienes({ ...filtros, pagina })
  const [texto, setTexto] = useState(filtros.q)
  const [seleccion, setSeleccion] = useState<Set<number>>(new Set())
  const [trasladando, setTrasladando] = useState(false)
  const [dandoDeBaja, setDandoDeBaja] = useState(false)
  const [agregando, setAgregando] = useState(false)
  const [etiquetas, setEtiquetas] = useState<Bien[] | null>(null)
  const descargar = useDescargarPlanilla()
  const paraEtiquetas = useParaEtiquetas()
  const marcarEtiquetas = useMarcarEtiquetas()
  // La selección es de la página que se ve: otro filtro u otra página la limpian.
  const claveFiltros = params.toString()
  useEffect(() => setSeleccion(new Set()), [claveFiltros])

  const poner = (cambios: Record<string, string | number>) => {
    const nuevos = new URLSearchParams(params)
    for (const [clave, valor] of Object.entries(cambios)) {
      if (valor === '' || valor === 1) nuevos.delete(clave)
      else nuevos.set(clave, String(valor))
    }
    if (!('pagina' in cambios)) nuevos.delete('pagina')
    setParams(nuevos, { replace: true })
  }

  // La búsqueda por texto espera a que se deje de escribir.
  useEffect(() => {
    const t = setTimeout(() => {
      if (texto !== (params.get('q') ?? '')) poner({ q: texto.trim() })
    }, 300)
    return () => clearTimeout(t)
  }, [texto]) // solo al escribir: `poner` y `params` cambian con cada filtro

  const hayFiltros = FILTROS.some((f) => params.get(f))
  const marcados = () => (bienes.data?.resultados ?? []).filter((b) => seleccion.has(b.id))
  // Las etiquetas pendientes de todo el filtro (no solo de esta página), por oficina y N°.
  const imprimirPendientes = () =>
    paraEtiquetas.mutate({ ...filtros, revisar: 'etiqueta_pendiente' }, { onSuccess: (lista) => lista.length && setEtiquetas(lista) })
  const marcarEtiqueta = (pendiente: boolean) =>
    marcarEtiquetas.mutate({ bienes: [...seleccion], pendiente }, { onSuccess: () => setSeleccion(new Set()) })
  return (
    <div className="pila pila--amplia">
      <header className="pagina__cabecera pagina__cabecera--con-accion">
        <div>
          <h1 className="dcc-t-heading-m">Bienes</h1>
          <p className="texto-secundario">Todo el inventario. Para revisar una oficina, entra a ella desde Oficinas.</p>
        </div>
        {admin && (
        <div className="acciones-pagina">
          <Boton icono={Plus} onClick={() => setAgregando(true)}>Agregar bienes</Boton>
          <Boton variante="outlined" icono={FileSpreadsheet} to="/app/bienes/planilla">Cargar planilla</Boton>
          <Boton
            variante="outlined"
            icono={Printer}
            onClick={imprimirPendientes}
            cargando={paraEtiquetas.isPending}
            title="Las etiquetas pendientes de los bienes de este filtro, para imprimirlas"
          >
            Etiquetas pendientes
          </Boton>
          <Boton
            variante="outlined"
            icono={Download}
            onClick={() => descargar.mutate({ ...filtros })}
            cargando={descargar.isPending}
            title="Los bienes de este filtro, para editarlos en Excel"
          >
            Descargar planilla
          </Boton>
        </div>
        )}
      </header>
      {descargar.isError && <Alerta tipo="error">{mensajeDeError(descargar.error)}</Alerta>}
      {paraEtiquetas.isError && <Alerta tipo="error">{mensajeDeError(paraEtiquetas.error)}</Alerta>}
      {paraEtiquetas.data?.length === 0 && (
        <Alerta tipo="info">{hayFiltros ? 'Ningún bien de este filtro tiene la etiqueta pendiente.' : 'No hay etiquetas pendientes.'}</Alerta>
      )}
      {marcarEtiquetas.isError && <Alerta tipo="error">{mensajeDeError(marcarEtiquetas.error)}</Alerta>}

      <div className="filtros">
        <Busqueda etiqueta="Buscar bienes" placeholder="N°, serie, descripción, marca o modelo" value={texto} onChange={(e) => setTexto(e.target.value)} />
        <Campo etiqueta="Tipo" ancho={false}>
          <Seleccion value={filtros.tipo} onChange={(e) => poner({ tipo: e.target.value })}>
            <option value="">Todos</option>
            {(Object.keys(TIPO) as TipoBien[]).map((t) => (
              <option key={t || 'SIN'} value={t || 'SIN'}>{TIPO[t].texto}</option>
            ))}
          </Seleccion>
        </Campo>
        <Campo etiqueta="Estado" ancho={false}>
          <Seleccion value={filtros.estado} onChange={(e) => poner({ estado: e.target.value })}>
            <option value="">Todos</option>
            {(Object.keys(ESTADO) as EstadoBien[]).map((t) => (
              <option key={t} value={t}>{ESTADO[t].texto}</option>
            ))}
          </Seleccion>
        </Campo>
        <Campo etiqueta="Estado físico" ancho={false}>
          <Seleccion value={filtros.estado_fisico} onChange={(e) => poner({ estado_fisico: e.target.value })}>
            <option value="">Cualquiera</option>
            {(Object.keys(ESTADO_FISICO) as EstadoFisico[]).map((t) => (
              <option key={t || 'SIN'} value={t || 'SIN'}>{ESTADO_FISICO[t].texto}</option>
            ))}
          </Seleccion>
        </Campo>
        <Campo etiqueta="Sector" ancho={false}>
          <Seleccion value={filtros.sector} onChange={(e) => poner({ sector: e.target.value })}>
            <option value="">Todos</option>
            {(Object.keys(SECTOR) as Sector[]).map((s) => (
              <option key={s} value={s}>{SECTOR[s]}</option>
            ))}
          </Seleccion>
        </Campo>
        <Campo etiqueta="Teletrabajo" ancho={false}>
          <Seleccion value={filtros.teletrabajo} onChange={(e) => poner({ teletrabajo: e.target.value })}>
            <option value="">Todos</option>
            <option value="si">En teletrabajo</option>
            <option value="no">Sin teletrabajo</option>
          </Seleccion>
        </Campo>
        <Campo etiqueta="Por revisar" ancho={false}>
          <Seleccion value={filtros.revisar} onChange={(e) => poner({ revisar: e.target.value })}>
            <option value="">—</option>
            {(Object.keys(REVISAR) as Revisar[]).map((t) => (
              <option key={t} value={t}>{REVISAR[t]}</option>
            ))}
          </Seleccion>
        </Campo>
        {hayFiltros && (
          <Boton
            variante="text"
            tam="sm"
            onClick={() => {
              setTexto('')
              setParams(new URLSearchParams(), { replace: true })
            }}
          >
            Limpiar filtros
          </Boton>
        )}
      </div>

      {filtros.recinto && <p className="texto-secundario">Solo los de la oficina {filtros.recinto}.</p>}
      {bienes.isLoading && <Cargando />}
      {bienes.isError && <Alerta tipo="error">{mensajeDeError(bienes.error)}</Alerta>}
      {bienes.data && (
        <>
          <BarraSeleccion
            texto={`${bienes.data.count.toLocaleString('es-CL')} ${bienes.data.count === 1 ? 'bien' : 'bienes'}`}
            marcados={seleccion.size}
            acciones={
              admin
                ? [
                    { clave: 'trasladar', texto: 'Trasladar', Icono: ArrowRightLeft, onClick: () => setTrasladando(true) },
                    { clave: 'imprimir', texto: 'Imprimir etiquetas', Icono: Printer, onClick: () => setEtiquetas(marcados()) },
                    { clave: 'baja', texto: 'Dar de baja', Icono: Archive, color: 'error', onClick: () => setDandoDeBaja(true) },
                    { clave: 'pegada', texto: 'Etiqueta pegada', Icono: Tag, secundaria: true, onClick: () => marcarEtiqueta(false) },
                    { clave: 'falta', texto: 'Falta etiqueta', Icono: TicketX, secundaria: true, onClick: () => marcarEtiqueta(true) },
                  ]
                : [{ clave: 'solicitar', texto: 'Solicitar traslado', Icono: ArrowRightLeft, onClick: () => setTrasladando(true) }]
            }
            onDesmarcar={() => setSeleccion(new Set())}
          />
          {bienes.data.resultados.length ? (
            <TablaBienes bienes={bienes.data.resultados} etiqueta="Bienes" mostrarRecinto seleccion={seleccion} onSeleccion={setSeleccion} />
          ) : (
            <p className="texto-secundario">Ningún bien calza con el filtro.</p>
          )}
          <Paginacion pagina={bienes.data.pagina} paginas={bienes.data.paginas} onCambiar={(p) => poner({ pagina: p })} />
        </>
      )}
      {agregando && <DialogoAgregarBienes onCerrar={() => setAgregando(false)} />}
      {etiquetas && <DialogoEtiquetas bienes={etiquetas} onCerrar={() => setEtiquetas(null)} />}
      {dandoDeBaja && bienes.data && (
        <DialogoBaja
          bienes={marcados()}
          onCerrar={() => setDandoDeBaja(false)}
          onHecho={() => setSeleccion(new Set())}
        />
      )}
      {trasladando && bienes.data && !admin && (
        <DialogoSolicitarTraslado bienes={marcados()} onCerrar={() => setTrasladando(false)} onHecho={() => setSeleccion(new Set())} />
      )}
      {trasladando && bienes.data && admin && (
        <DialogoTraslado
          bienes={marcados()}
          onCerrar={() => setTrasladando(false)}
          onHecho={() => setSeleccion(new Set())}
        />
      )}
    </div>
  )
}
